package ai.wagemix.web.theme

import com.fsryan.foundation.ui.tokens.FSColorTokens

/**
 * The Wagemix brand as foundation colour tokens: every value is the `fs.color.*` token of the
 * Penpot "Wagemix New Site" file (sets `wagemix/color/light` and `wagemix/color/dark`,
 * exported 2026-10-23; see `planning/tokens/wagemix-tokens.json`). The redesign uses one blue
 * for every action, teal for AI/projected values, amber for warnings and slate neutrals.
 *
 * Roles the Penpot file does not declare keep the foundation default through delegation.
 */
object WMColorTokens {

    val light: FSColorTokens = object: FSColorTokens by FSColorTokens.light {
        override val primary = 0xFF2563EB
        override val onPrimary = 0xFFFFFFFF
        override val primaryContainer = 0xFFEFF4FF
        override val onPrimaryContainer = 0xFF1E3A8A
        override val inversePrimary = 0xFF93C5FD
        override val secondary = 0xFF0F766E
        override val onSecondary = 0xFFFFFFFF
        override val secondaryContainer = 0xFFE6F7F5
        override val onSecondaryContainer = 0xFF115E59
        override val tertiary = 0xFFB45309
        override val onTertiary = 0xFFFFFFFF
        override val tertiaryContainer = 0xFFFFF7E6
        override val onTertiaryContainer = 0xFF92400E
        override val background = 0xFFF8FAFB
        override val onBackground = 0xFF0F172A
        override val surface = 0xFFFFFFFF
        override val onSurface = 0xFF0F172A
        override val surfaceVariant = 0xFFF1F5F9
        override val onSurfaceVariant = 0xFF475569
        override val surfaceTint = 0xFF2563EB
        override val inverseSurface = 0xFF0F172A
        override val inverseOnSurface = 0xFFF8FAFB
        override val error = 0xFFDC2626
        override val onError = 0xFFFFFFFF
        override val errorContainer = 0xFFFEF2F2
        override val onErrorContainer = 0xFF991B1B
        override val outline = 0xFFCBD5E1
        override val outlineVariant = 0xFFE2E8F0
        override val scrim = 0x660F172AL
        override val surfaceBright = 0xFFFFFFFF
        override val surfaceDim = 0xFFE2E8F0
        override val surfaceContainer = 0xFFF1F5F9
        override val surfaceContainerHigh = 0xFFE2E8F0
        override val surfaceContainerHighest = 0xFFCBD5E1
        override val surfaceContainerLow = 0xFFF8FAFB
        override val surfaceContainerLowest = 0xFFFFFFFF
        override val inactive = 0xFFE2E8F0
        override val onInactive = 0xFF94A3B8
        override val success = 0xFF16A34A
        override val topBarContainer = 0xFFFFFFFF
        override val onTopBarContainer = 0xFF0F172A
    }

    val dark: FSColorTokens = object: FSColorTokens by FSColorTokens.dark {
        override val primary = 0xFF60A5FA
        override val onPrimary = 0xFF0B1220
        override val primaryContainer = 0xFF1E2A4A
        override val onPrimaryContainer = 0xFFDBEAFE
        override val inversePrimary = 0xFF2563EB
        override val secondary = 0xFF2DD4BF
        override val onSecondary = 0xFF042F2E
        override val secondaryContainer = 0xFF123B38
        override val onSecondaryContainer = 0xFFCCFBF1
        override val tertiary = 0xFFFBBF24
        override val onTertiary = 0xFF1C1300
        override val tertiaryContainer = 0xFF3A2A10
        override val onTertiaryContainer = 0xFFFEF3C7
        override val background = 0xFF101317
        override val onBackground = 0xFFE9EEF2
        override val surface = 0xFF181C20
        override val onSurface = 0xFFE9EEF2
        override val surfaceVariant = 0xFF23272B
        override val onSurfaceVariant = 0xFF9AA5B1
        override val surfaceTint = 0xFF60A5FA
        override val inverseSurface = 0xFFE9EEF2
        override val inverseOnSurface = 0xFF101317
        override val error = 0xFFF87171
        override val onError = 0xFF1A0505
        override val errorContainer = 0xFF3B1414
        override val onErrorContainer = 0xFFFECACA
        override val outline = 0xFF3A434F
        override val outlineVariant = 0xFF2C3440
        override val scrim = 0x99000000L
        override val surfaceBright = 0xFF2C3440
        override val surfaceDim = 0xFF0B0E11
        override val surfaceContainer = 0xFF1E2328
        override val surfaceContainerHigh = 0xFF2C3440
        override val surfaceContainerHighest = 0xFF3A434F
        override val surfaceContainerLow = 0xFF14181C
        override val surfaceContainerLowest = 0xFF0B0E11
        override val inactive = 0xFF2C3440
        override val onInactive = 0xFF6B7785
        override val success = 0xFF4ADE80
        override val topBarContainer = 0xFF181C20
        override val onTopBarContainer = 0xFFE9EEF2
    }
}
